import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function WishFlowPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="WishFlow"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “WishFlow” (the “App”) collects, uses,
        stores, and discloses information about users and shared content. The
        App lets you create and manage wishlists, add items via link or manual
        entry, attach photos, share lists with friends, view friends’ shared
        lists, and (optionally) subscribe for premium themes, hidden lists, and
        higher limits. Core storage uses Firebase Firestore and Firebase Storage
        with an offline cache on your device. This Policy covers onboarding and
        sign-in (including Guest), wishlist and item data, link parsing via
        serverless functions, media uploads, sharing controls, subscriptions,
        notifications, analytics, crash diagnostics, and support. By using the
        App, you acknowledge this Policy and agree to its terms. If you
        disagree, uninstall the App and contact us about your data options.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller that determines the purposes and means of processing for
        the App is Aleksandra Rakitina. For privacy questions or to exercise
        rights such as access, deletion, correction, restriction, objection, or
        data portability, contact alexwadek@gmail.com. We verify your identity
        before acting on requests, typically by confirming control of the
        account email or requesting limited additional details. We retain
        privacy correspondence for compliance and security documentation where
        allowed by law.
      </p>

      <div className={s.title}>
        3) Key Principles (User Control, Minimization, Transparency)
      </div>
      <p>
        User control: you decide which lists and items to create, what to share,
        and with whom. Minimization: we collect only what is needed to deliver
        features (auth, synchronized storage, sharing, subscriptions, reliability
        analytics). Transparency: we explain how link parsing, media uploads,
        and sharing work, and we identify our service providers. We do not sell
        Personal Data. Advertising SDKs for behavioral profiling are not used.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Wishlist Data” includes list titles, covers,
        descriptions, item names/notes/quantities, links/URLs, price snapshots,
        tags, and custom fields you add. “Media” means images you upload (e.g.,
        item photos, covers). “Share Settings” are visibility and access rules
        (e.g., private/hidden/shared) and identifiers for invitees.
        “Processing” includes collection, storage, transformation, sharing, and
        deletion. “Service Providers/Processors” are third parties acting on our
        instructions (Firebase, RevenueCat, analytics, notifications).
      </p>

      <div className={s.title}>5) What We Collect</div>
      <p>
        Depending on your choices and device settings, we process: (a) Account
        data via Firebase Auth (email, display name, provider IDs; Guest
        accounts store a local ID until you link an email/provider to back up
        data); (b) Wishlist Data you create or import, including links and
        optional notes; (c) Media uploaded to illustrate items/lists; (d) Share
        Settings and access logs needed to administer invitations and permission
        checks; (e) Link parsing results (page title, images, price text,
        merchant name, currency) retrieved by Firebase Functions when you add a
        URL; (f) Subscription and entitlement metadata from RevenueCat (product
        IDs, trial status, renewal outcome, expiration/cancellation) to grant
        premium features; (g) Analytics and crash diagnostics (event counters,
        anonymized error codes, device/OS/app versions) for reliability; (h)
        Notification tokens and preferences for push (FCM). We do not receive
        payment card numbers.
      </p>

      <div className={s.title}>6) Sources of Data</div>
      <p>
        Most data comes from you (lists, items, Media, and sharing choices).
        Parsing data is fetched from the website you link to, using a Cloud
        Function that requests page metadata and, where feasible, a price
        snapshot. Technical and diagnostic data is generated automatically by
        the App and service providers to keep things stable and secure.
      </p>

      <div className={s.title}>7) Offline Cache and Sync</div>
      <p>
        The App maintains an offline cache (SQLite/Hive) so you can view and
        edit lists without a connection. When the network returns, changes sync
        to Firestore under your account. On Guest, data is only on-device; if
        you delete the App or reset the device, Guest data may be lost. Linking
        your account moves data under authenticated storage for cross-device
        access.
      </p>

      <div className={s.title}>8) Link Parsing and Price Snapshots</div>
      <p>
        When you paste a product URL, a Firebase Function fetches minimal page
        metadata (title, main image where available, merchant label, price
        text/currency) to prefill an item. We do not complete purchases on your
        behalf. Parsed data can be inaccurate or change; always verify before
        buying. We do not attach advertising IDs to parsing requests. If a site
        blocks automated fetches, parsing may fail, and you can enter details
        manually.
      </p>

      <div className={s.title}>9) Media Uploads</div>
      <p>
        Item photos and covers you add are stored in Firebase Storage linked to
        your account. Uploads occur over HTTPS. We generate resized previews for
        faster loading. Deleting an item or list removes its references;
        underlying blobs are scheduled for deletion subject to provider
        retention and replication windows.
      </p>

      <div className={s.title}>
        10) Sharing, Visibility, and Hidden Lists (Premium)
      </div>
      <p>
        Lists can be private, shared via invite links, or—if offered—made
        viewable by specific accounts. Premium may include Hidden Lists, which
        are excluded from general list views and require explicit access. Shared
        content is visible to recipients according to permissions you set
        (view/edit, where available). When you share by link, anyone with that
        link may access until you revoke or regenerate it. For safety, review
        recipients periodically and avoid posting invite links publicly. Hidden
        Lists enhance privacy but are not a confidentiality guarantee if you
        disclose links.
      </p>

      <div className={s.title}>11) What We Do Not Collect or Sell</div>
      <p>
        We do not sell Personal Data. We do not collect your payment card
        numbers. We do not record precise geolocation for core features. We do
        not inspect your purchases on merchant sites. We do not embed trackers
        in exports or images. We do not build third-party advertising profiles
        from Wishlist Data.
      </p>

      <div className={s.title}>12) Subscriptions, Trials, and Entitlements</div>
      <p>
        Subscriptions use platform billing via RevenueCat and may include a
        3-day free trial. We receive limited metadata (product identifiers,
        trial eligibility, renewal results, expiration dates, cancellation
        status) to grant or revoke premium features such as Hidden Lists,
        Themes, or Unlimited items. Manage or cancel in your device account
        settings; refunds and grace periods follow platform rules and applicable
        law.
      </p>

      <div className={s.title}>13) Notifications</div>
      <p>
        With your permission, we may send push notifications (e.g., collaborator
        invites, list activity, reminders). Delivery requires a device token
        used only to route messages. You can disable notifications in system
        settings or in-app toggles where available. We avoid placing sensitive
        Wishlist Data in notification bodies.
      </p>

      <div className={s.title}>14) Analytics and Crash Reporting</div>
      <p>
        We use Firebase Analytics and Crashlytics, and may receive RevenueCat
        event webhooks/aggregations, to understand reliability and feature
        adoption—not to build third-party advertising profiles. Typical events:
        onboarding_completed, wishlist_created, item_added (via link/manual),
        list_shared, invite_opened, paywall_opened, trial_started,
        purchase_success, theme_applied. Where feasible, analytics are
        aggregated or pseudonymized and avoid full item content. Platform
        privacy controls can reduce analytics, which may limit troubleshooting.
      </p>

      <div className={s.title}>
        15) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        Legal bases include performance of a contract (account, storage,
        sharing, parsing, subscriptions), legitimate interests (security, fraud
        prevention, product improvement) balanced against your rights through
        minimization and controls, consent (notifications and certain analytics
        categories where required), and legal obligations (records, lawful
        requests). We will seek additional consent if purposes materially
        change.
      </p>

      <div className={s.title}>16) Security Measures</div>
      <p>
        We apply layered safeguards: HTTPS for transit; encryption at rest for
        Firestore/Storage; rules-based access control for documents and media;
        least-privilege access to operational tools; environment separation;
        credential rotation; monitoring for abuse. No system is perfectly
        secure, but we reduce exposure through minimization, strong defaults
        (private lists by default), and user controls (revoking links, deleting
        content).
      </p>

      <div className={s.title}>17) Data Retention and Deletion</div>
      <p>
        Your Wishlist Data remains until you delete it or close your account.
        When you delete a list or item, we remove it from Firestore and schedule
        associated Media for deletion; provider backups and replication may
        introduce short delays before complete purge. Subscription/entitlement
        metadata may be retained longer for accounting and fraud prevention in
        pseudonymized or aggregated form. Crash/analytics logs are kept for
        operational trend windows and then de-identified or deleted. Verified
        deletion requests are processed within a reasonable timeframe.
      </p>

      <div className={s.title}>18) International Data Transfers</div>
      <p>
        Depending on your and our providers’ locations, data may be processed in
        countries with different data-protection rules. Where required, we rely
        on appropriate safeguards for cross-border transfers (contractual
        protections and platform mechanisms). Providers must implement security
        measures consistent with this Policy and applicable law.
      </p>

      <div className={s.title}>19) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app, you can
        rename/delete lists, remove items, revoke share links, change themes,
        and disable notifications. For account-level requests, email
        alexwadek@gmail.com from your sign-in address. We will verify your
        identity and respond within a reasonable time. If we cannot fulfill a
        request due to legal or technical limits, we will explain unless
        prohibited.
      </p>

      <div className={s.title}>20) Do Not Track, Identifiers, and Cookies</div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers for authentication, storage, analytics, crash reporting,
        notifications, and subscriptions. These function similarly to cookies by
        enabling sessions and measurement. The App does not rely on advertising
        identifiers for targeted ads. Browser “Do Not Track” signals generally
        do not apply to native apps.
      </p>

      <div className={s.title}>
        21) Collaborations, Invites, and External Destinations
      </div>
      <p>
        When you invite others, we process minimal contact or link data
        necessary to deliver the invitation. If you share exported images or
        links through messaging or social platforms, those destinations have
        their own privacy policies. Deleting a list in the App does not delete
        copies or screenshots your collaborators may have saved elsewhere.
      </p>

      <div className={s.title}>22) Guest Mode and Account Linking</div>
      <p>
        Guest use stores data only on your device. If you uninstall or reset the
        device, Guest data may be lost and cannot be restored. Linking your
        Guest profile to Apple/Google/Email creates an authenticated account and
        moves data to synchronized storage for backup and multi-device access.
      </p>

      <div className={s.title}>23) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. If you believe a minor used the App without proper
        authorization, contact alexwadek@gmail.com and we will remove
        information and restrict access as appropriate. Device-level controls
        can limit purchases and notifications.
      </p>

      <div className={s.title}>
        24) Fair Use, Abuse Prevention, and Limits
      </div>
      <p>
        To maintain reliability and costs, we may apply fair-use thresholds
        (e.g., excessive automated link parsing or mass media uploads). Attempts
        to bypass limits or misuse sharing may trigger protective measures such
        as rate-limits or temporary restriction. We will communicate material
        changes to limits according to platform rules and applicable law.
      </p>

      <div className={s.title}>
        25) Third-Party Providers and Disclosures
      </div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase (Auth,
        Firestore, Storage, Functions, FCM), RevenueCat (subscriptions), Firebase
        Analytics/Crashlytics, and email support infrastructure. Providers are
        bound by terms requiring appropriate security and prohibiting independent
        marketing use of your Personal Data. We may disclose information when
        legally required by competent authorities after validating scope and
        jurisdiction, or in a business transition where a successor honors this
        Policy or issues an updated one with notice.
      </p>

      <div className={s.title}>
        26) Accuracy, Responsibility, and Merchant Policies
      </div>
      <p>
        Wishlist links lead to third-party merchants with their own terms and
        privacy practices. Prices and availability change; parsed data is a
        convenience snapshot, not a guarantee. Always review merchant details
        before purchasing. The App does not process payments for items and is
        not responsible for third-party order fulfillment or returns.
      </p>

      <div className={s.title}>27) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes affecting your rights or our processing will be
        highlighted in-app; where required, we will seek consent. Continued use
        after changes take effect signifies acceptance. If you disagree with
        updates, stop using the App and request deletion of account-linked
        records we control, subject to verification and applicable retention
        obligations.
      </p>

      <div className={s.title}>
        28) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com from your sign-in
        address and specify the request type (access, deletion, correction,
        restriction, objection, portability). Include helpful context such as
        device model and app version. We verify identity to prevent unauthorized
        access or deletion and may request limited additional information or a
        confirmation code. Where feasible, data is provided in a portable,
        machine-readable format. We may decline or limit requests when permitted
        by law (e.g., if fulfilling a request would compromise security,
        interfere with legal obligations, or infringe others’ privacy), and we
        will explain our reasoning unless prohibited.
      </p>
    </PolicyLayout>
  );
}
